﻿import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Card,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  FormGroup,
  FormHelperText,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  selectSelectedConnection,
  setSharedCatalogSetupDetails,
  setConnectionPairObject,
  fetchConnectionAllPairs,
} from "@/features/connections/integrationSlice";
import { useDispatch, useSelector } from "react-redux";
import { ShepCheckbox } from "@/components/GenericComponents/ShepComponents";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { createAlert } from "@/features/homepage/alertSliceList";
import axios from "@/features/axiosHelper";

const SharedCatalogs = ({ open, onClose, rowData }) => {
  const dispatch = useDispatch();

  const selectedConnection = useSelector(selectSelectedConnection);
  const [selectedCatalogs, setSelectedCatalogs] = React.useState([]);
  const [primaryConnection, setPrimaryConnection] = useState("");
  const [secondaryConnection, setSecondaryConnection] = useState("");
  const [primaryConnectionError, setPrimaryConnectionError] = useState(false);
  const [catalogSelectionErrorr, setCatalogSelectionError] = useState(false);

  const catalogTypes = [
    { label: "Customer", value: 0 },
    { label: "Item", value: 1 },
    { label: "UOM", value: 2 },
    { label: "Warehouse", value: 3 },
    { label: "Ship To", value: 4 },
    { label: "Carrier", value: 5 },
    { label: "Misc. Charges", value: 6 },
  ];
  const isEverythingSelected = selectedCatalogs.length === catalogTypes.length;
  useEffect(() => {
    if (rowData?.sharedCatalogInfo) {
      const parsedData = JSON.parse(rowData.sharedCatalogInfo);
      const settings = parsedData.SharedCatalogSettings;
      if (settings.PrimaryConnectionId) {
        setPrimaryConnection(settings.PrimaryConnectionId);
      }

      if (settings.SecondaryConnectionId) {
        setSecondaryConnection(settings.SecondaryConnectionId);
      }

      if (Array.isArray(settings.SelectedCatalogs)) {
        setSelectedCatalogs(settings.SelectedCatalogs);
      }
    } else {
      setSelectedCatalogs([]);
      setPrimaryConnection("");
      setSecondaryConnection("");
    }
  }, [rowData, open]);

  // Form the dropdown list only if both connections are available
  const dropdownOptions =
    rowData && selectedConnection
      ? [
          {
            label: rowData.ConnectionName,
            value: rowData.ConnectionId,
          },
          {
            label: selectedConnection.ConnectionName,
            value: selectedConnection.ConnectionId,
          },
        ]
      : [];

  const handleConnectionChange = (event) => {
    const primaryConnection = event.target.value;
    setPrimaryConnection(primaryConnection);
    setPrimaryConnectionError(false);

    //find the non-selected value
    const secondaryOption = dropdownOptions.find(
      (option) => option.value !== primaryConnection
    );

    if (secondaryOption) {
      setSecondaryConnection(secondaryOption.value);
    }
  };

  const handleToggle = (value) => {
    if (selectedCatalogs.includes(value)) {
      setSelectedCatalogs(selectedCatalogs.filter((v) => v !== value));
    } else {
      setSelectedCatalogs([...selectedCatalogs, value]);
    }
  };

  const handleSelectAll = () => {
    setCatalogSelectionError(false);
    if (isEverythingSelected) {
      // All items are selected → deselect everything
      setSelectedCatalogs([]);
    } else {
      // Not all items are selected → select all items
      setSelectedCatalogs(catalogTypes.map((r) => r.value));
    }
  };

  const handlePopUpClose = () => {
    setSelectedCatalogs([]);
    setPrimaryConnection("");
    setSecondaryConnection("");
    setPrimaryConnectionError(false);
    setCatalogSelectionError(false);
    onClose();
  };

  const applySettings = () => {
    // validation
    if (!primaryConnection) {
      setPrimaryConnectionError(true);
      return;
    }

    if (rowData.hasOwnProperty("connectionPairId")) {
      const connectionPairObj = {
        ConnectionId1: selectedConnection.ConnectionId,
        ConnectionId2: rowData.ConnectionId,
        ConnectionPairName: rowData.connectionPairName,
        ConnectionPairId: rowData.connectionPairId,
        Status: rowData.status,
      };
      dispatch(
        setConnectionPairObject({
          connectionPairObject: connectionPairObj,
        })
      );
    }

    const catalogSharingSetupInfo = {
      PrimaryConnectionId: primaryConnection,
      SecondaryConnectionId: secondaryConnection,
      SelectedCatalogs: selectedCatalogs,
      ConnectionId1: selectedConnection.ConnectionId,
      ConnectionId2: rowData.ConnectionId,
    };

    dispatch(
      setSharedCatalogSetupDetails({
        catalogSharingSetupDetails: catalogSharingSetupInfo,
      })
    );

    handlePopUpClose();
  };
  return (
    <>
      <Dialog
        className="workflow-settings-container"
        open={open}
        onClose={handlePopUpClose}
        maxWidth="md"
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            boxShadow: "none",
            minWidth: "45vw",
          },
        }}
        sx={{ zIndex: 2000 }}
      >
        <DialogTitle
          className="workflow-settings-heading"
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            fontSize: "24px",
            borderRadius: "15px 15px 0 0",
          }}
        >
          {rowData?.sharedCatalogInfo ? (
            <>
              <div style={{ display: "flex" }}>
                Update Catalog Sharing Setup
              </div>
            </>
          ) : (
            <>
              {" "}
              <div style={{ display: "flex" }}>Add Catalog Sharing Setup</div>
            </>
          )}
          <IconButton
            className="closeIconButton"
            style={{ color: "var(--default-light)" }}
            onClick={handlePopUpClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          className="dialogContent"
          style={{
            borderRadius: "0 0 16px 16px",
            padding: "0px",
          }}
        >
          <Card
            style={{
              marginTop: "10px",
              marginLeft: "10px",
              marginRight: "10px",
              boxShadow: "none",
              backgroundColor: "var(--default-light)",
            }}
          >
            <Box>
              <Typography variant="body3">Primary Connection</Typography>{" "}
              <span style={{ color: "red" }}>*</span>
              <Box marginTop={3}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ width: 400, marginBottom: "30px" }}
                  error={primaryConnectionError}
                >
                  <InputLabel id="primary-connection-label">
                    Select Primary Connection
                  </InputLabel>
                  <Select
                    labelId="primary-connection-label"
                    id="connection-list"
                    value={primaryConnection}
                    onChange={handleConnectionChange}
                    label="Primary Connection"
                    MenuProps={{
                      style: { zIndex: 2100 },
                    }}
                  >
                    {dropdownOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {primaryConnectionError && (
                    <FormHelperText style={{ color: "var(--error-primary)" }}>
                      Please select a primary connection{" "}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>
            <Box>
              <Typography variant="body3">Select Catalogs to Share </Typography>
              <span style={{ color: "red" }}>*</span>
              {catalogSelectionErrorr && (
                <FormHelperText sx={{ color: "var(--error-primary)" }}>
                  Please select at least one catalog to share
                </FormHelperText>
              )}
              <Box marginTop={3}>
                {/* Select All */}
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={
                    <ShepCheckbox
                      checked={isEverythingSelected}
                      indeterminate={
                        selectedCatalogs.length > 0 && !isEverythingSelected
                      }
                      onChange={handleSelectAll}
                    />
                  }
                  label="Select All"
                />

                {/* Stack checkboxes vertically */}
                <FormGroup sx={{ flexDirection: "column" }}>
                  {catalogTypes.map((ref) => (
                    <Box
                      key={ref.value}
                      display="flex"
                      alignItems="center"
                      mb={1}
                    >
                      <ShepCheckbox
                        checked={selectedCatalogs.includes(ref.value)}
                        onChange={() => {
                          handleToggle(ref.value);
                          setCatalogSelectionError(false);
                        }}
                      />
                      <Typography variant="body1" sx={{ marginLeft: 1 }}>
                        {ref.label}
                      </Typography>
                    </Box>
                  ))}
                </FormGroup>
              </Box>
            </Box>
          </Card>
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ marginTop: 3, marginRight: 3, marginBottom: 3 }}
          >
            <ShepButton variant="primary" onClick={applySettings}>
              Apply
            </ShepButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SharedCatalogs;
